<template>
  <div class="datepicker-container">
    <VueDatePicker
      :id="id"
      ref="datepicker"
      v-model="internalValue"
      :locale="useCurrentLocaleCode()"
      :format-locale="useDateFnsLocales()"
      class="relative h-full w-full"
      :flow="flow"
      :format="dateFormat"
      :clearable="false"
      hide-input-icon
      :text-input="{
        enterSubmit: true,
        tabSubmit: true,
        openMenu: 'open',
        rangeSeparator: '-',
        format: textInputFormatter,
      }"
      :inline="inline"
      :auto-apply="autoApply"
      :time-picker-inline="timePickerInline"
      :enable-time-picker="enableTimePicker"
      :month-change-on-scroll="false"
      :enable-seconds="enableSeconds"
      teleport
      :placeholder="$t('common.datepicker_placeholder')"
      :disabled="disabled"
      select-text="Select"
      :ui="{ input: inputClassName }"
      :name="name"
      utc="preserve"
      :min-date="minDate"
      :allowed-dates="enabledDates"
      :multi-calendars="multiCalendars"
      :range="rangeConfig"
      :hide-offset-dates="hideOffsetDates"
      @keydown.enter.prevent
      @update:model-value="formatTextDate"
      @flow-step="flowChanged"
      @date-update="onDateClick"
      @invalid-date="onInvalidDate"
    >
      <template v-if="$slots.day" #day="{ day, date }">
        <slot name="day" :day="day" :date="date" />
      </template>
    </VueDatePicker>
  </div>
</template>

<script setup>
import VueDatePicker from '@vuepic/vue-datepicker'
import { ref, watch, computed } from 'vue'
import { useCurrentLocaleCode, useDateFnsLocales } from '@/composables/locale'
import { parseDate } from '@/composables/date'
import { format } from 'date-fns'
import { toZonedTime, fromZonedTime } from 'date-fns-tz'

const props = defineProps({
  dateFormat: {
    type: String,
    required: false,
    default: 'Pp',
  },
  flow: {
    type: Array,
    required: false,
    default: () => [],
  },
  inline: {
    type: Boolean,
    required: false,
    default: false,
  },
  enableTimePicker: {
    type: Boolean,
    required: false,
    default: false,
  },
  enableSeconds: {
    type: Boolean,
    required: false,
    default: false,
  },
  timePickerInline: {
    type: Boolean,
    required: false,
    default: true,
  },
  autoApply: {
    type: Boolean,
    required: false,
    default: false,
  },
  minDate: {
    type: Date,
    required: false,
    default: null,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  inputClassName: {
    type: String,
    required: false,
    default:
      'rounded border border-[#e0e0e0] p-[7px_15px] text-[13px] text-[#3d4146] relative bg-white h-full',
  },
  timeZone: {
    type: String,
    required: false,
    default: 'UTC',
  },
  name: {
    type: String,
    required: false,
    default: null,
  },
  id: {
    type: String,
    required: false,
    default: null,
  },
  enabledDates: {
    // allowed-dates is an ALLOW-list: [] would disable every date, so an unset
    // prop must stay undefined (library default = no restriction). Consumers
    // may still pass [] deliberately to disable everything (e.g. no bookable
    // slots yet).
    type: Array,
    required: false,
    default: undefined,
  },
  multiCalendars: {
    type: Boolean,
    default: () => false,
  },
  range: {
    type: Boolean,
    default: () => false,
  },
  minRange: {
    type: Number,
    required: false,
    default: 1,
  },
  hideOffsetDates: {
    type: Boolean,
    default: () => false,
  },
})

const emit = defineEmits(['date-clicked'])

const model = defineModel({ type: [String, Date, Array] })

const rangeConfig = computed(() => {
  if (!props.range) return false
  return {
    minRange: props.minRange,
    noDisabledRange: true,
  }
})

const internalValue = ref(null)
const datepicker = ref(null)

const stripMilliseconds = (value) => value?.replace(/\..*$/, '')

const toInternalFormat = (date) =>
  format(toZonedTime(new Date(date), props.timeZone), "yyyy-MM-dd'T'HH:mm:ss") +
  '.000Z'

const toModelFormat = (date) =>
  fromZonedTime(new Date(stripMilliseconds(date)), props.timeZone).toISOString()

// The helpers above must be initialized before this watcher: it runs
// immediately, so a prefilled model converts during setup.
watch(
  () => model.value,
  (value) => {
    if (!value) {
      internalValue.value = null
      return
    }

    internalValue.value = props.range
      ? value.map(toInternalFormat)
      : toInternalFormat(value)
  },
  { immediate: true },
)

const formatTextDate = (value) => {
  internalValue.value = value

  model.value = !value
    ? null
    : props.range
      ? value.map(toModelFormat)
      : toModelFormat(value)
}

const flowChanged = (step) => {
  if (step === props.flow.length) {
    datepicker.value.selectDate()
  }
}

const onDateClick = (date) => {
  if (date) {
    emit('date-clicked', format(new Date(date), 'yyyy-MM-dd'))
  }
}

const onInvalidDate = () => {
  // Auto-reset partial range selection when user clicks on a disabled date
  // This allows users to escape from stuck states (e.g., isolated enabled date)
  if (props.range) {
    datepicker.value?.updateInternalModelValue(null)
    internalValue.value = null
    model.value = null
  }
}

// Note: required Text Input parser function, triggers by changing date by text.
const textInputFormatter = (value) => {
  let formatLong = useDateFnsLocales().formatLong
  let dateFormatString = formatLong.date({ width: 'short' })
  let timeFormatString = formatLong.time({
    width: props.enableSeconds ? 'medium' : 'short',
  })
  let dateTimeFormatString = formatLong.dateTime({
    width: props.enableSeconds ? 'medium' : 'short',
  })
  let replacements = {
    '{{date}}': dateFormatString,
    '{{time}}': timeFormatString,
  }
  let fullFormatString = props.enableTimePicker
    ? dateTimeFormatString.replace(
        /{{date}}|{{time}}/g,
        (matched) => replacements[matched],
      )
    : dateFormatString

  return parseDate(value, fullFormatString, new Date())
}

defineExpose({
  focus: () => datepicker.value.openMenu(),
  clear: () => {
    datepicker.value?.updateInternalModelValue(null)
    internalValue.value = null
    model.value = null
  },
})
</script>
